import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiHeadphones } = FiIcons;

const Hero = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const videos = [
    { id: 'vqetNtiKZp8', title: 'Boss At Heart Short 1' },
    { id: 'IwnK46dX27I', title: 'Boss At Heart Short 2' },
    { id: 'kiK3zUJMrLE', title: 'Boss At Heart Short 3' }
  ];

  return (
    <section className="section min-h-screen relative flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800 py-16 sm:py-20 md:py-24">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 floating-heart">❤️</div>
        <div className="absolute top-40 right-32 floating-heart" style={{ animationDelay: '2s' }}>❤️</div>
        <div className="absolute bottom-32 left-1/4 floating-heart" style={{ animationDelay: '4s' }}>❤️</div>
      </div>

      {/* Hero Heart Overlay */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.1 }}
        transition={{ duration: 2 }}
      >
        <div className="text-red-500 text-[10rem] sm:text-[15rem] md:text-[20rem] pulse-heart">❤️</div>
      </motion.div>

      <div className="container text-center text-white relative z-10 px-4">
        {/* Logo */}
        <motion.div
          className="mb-6 sm:mb-8"
          initial={{ y: -50, opacity: 0, scale: 0.8 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          whileHover={{ scale: 1.05, rotate: 5 }}
        >
          <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 mx-auto mb-3 sm:mb-4 rounded-full overflow-hidden shadow-2xl ring-4 ring-red-500/50 relative">
            <motion.img
              src="https://cloud-1de12d.becdn.net/media/original/e2094d1e58ff8b44189fe6562e9a7131.jpg"
              alt="Boss At Heart Logo"
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.3 }}
            />
            {/* Animated ring effect */}
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-red-400 opacity-0"
              animate={{
                opacity: [0, 0.5, 0],
                scale: [1, 1.2, 1.4],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeOut"
              }}
            />
          </div>
          <motion.h3
            className="text-lg sm:text-xl md:text-2xl font-bold text-red-500 tracking-wider"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            BOSS AT HEART
          </motion.h3>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          className="text-4xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6 leading-tight px-2"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Real Stories. Raw Truth. <span className="text-red-500">Big Heart.</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          className="text-lg sm:text-xl md:text-2xl mb-8 sm:mb-10 max-w-4xl mx-auto text-gray-300 leading-relaxed px-4"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          A podcast that turns pain into power and purpose — hosted by <span className="font-semibold text-white">Cam Go Crazy</span>. Every episode dives deep into the moments that shape us, reminding you that being a boss starts at heart.
        </motion.p>

        {/* Video Shorts Grid */}
        <motion.div
          className="mb-8 sm:mb-10 max-w-5xl mx-auto"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {videos.map((video, index) => (
              <motion.div
                key={video.id}
                className="relative aspect-[9/16] rounded-xl overflow-hidden border-4 border-red-500 shadow-2xl group cursor-pointer"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                {/* Recording Badge */}
                <div className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 z-10 animate-pulse">
                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                  REC
                </div>

                {/* Video Iframe */}
                <iframe
                  src={`https://www.youtube.com/embed/${video.id}?autoplay=1&mute=1&loop=1&playlist=${video.id}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  className="w-full h-full object-cover"
                  style={{ pointerEvents: 'none' }}
                />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiPlay} className="text-white text-xl ml-0.5" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Ember Glow Tagline */}
        <motion.p
          className="text-base sm:text-lg md:text-xl mb-10 sm:mb-12 max-w-2xl mx-auto px-4 font-medium relative"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          style={{
            textShadow: '0 0 20px rgba(255, 107, 53, 0.8), 0 0 40px rgba(255, 107, 53, 0.5), 0 0 60px rgba(255, 69, 0, 0.3)',
            color: '#FFD700',
            background: 'linear-gradient(to bottom, #FFD700 0%, #FF8C00 50%, #FF4500 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'ember-pulse 3s ease-in-out infinite'
          }}
        >
          "For the dreamers, the underdogs, and the ones still fighting their way up."
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 pb-8 sm:pb-12"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.3 }}
        >
          <a
            href="https://www.youtube.com/@camgocrazy_bossatheart/videos"
            target="_blank"
            rel="noopener noreferrer"
            className="btn w-full sm:w-auto inline-flex items-center justify-center"
          >
            <SafeIcon icon={FiPlay} />
            <span className="hidden sm:inline">Watch the Latest Episode</span>
            <span className="sm:hidden">Watch Episode</span>
          </a>
          <a
            href="https://open.spotify.com/show/64NgEdsEYrkLbKb5usj3eI?si=fc2ee0faf1e44039"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary w-full sm:w-auto inline-flex items-center justify-center"
          >
            <SafeIcon icon={FiHeadphones} />
            <span className="hidden sm:inline">Listen on Spotify</span>
            <span className="sm:hidden">Listen Now</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;